import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Plus,
  Upload,
  Search,
  Trash2,
  Mail,
  Phone,
  Building,
  MoreVertical,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  UserPlus,
  Loader2,
  X,
  Check,
  CloudOff,
  Cloud,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { API_BASE, mauticApi, audiencesApi, internalContactsApi as contactsApi } from '../lib/api';
import axios from 'axios';

// API functions
// Local API definitions removed, using centralized lib/api.js

// Import Wizard Component
const ImportWizard = ({ open, onClose, onComplete, audiences, onRefreshAudiences }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState('');
  const [newAudienceName, setNewAudienceName] = useState('');
  const [createNewAudience, setCreateNewAudience] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setSessionId(null);
    setUploadResult(null);
    setAnalysisResult(null);
    setUpdateExisting(false);
    setSelectedAudience('');
    setNewAudienceName('');
    setCreateNewAudience(false);
    setImportResult(null);
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(csv|xlsx|xls)$/i)) {
        toast.error('Formato no soportado. Use CSV o Excel (.xlsx)');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Step 1: Upload file
  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const response = await contactsApi.upload(file);
      setUploadResult(response.data);
      setSessionId(response.data.sessionId);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Analyze contacts
  const handleAnalyze = async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const response = await contactsApi.analyze(sessionId);
      setAnalysisResult(response.data);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al analizar contactos');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Choose update option
  const handleUpdateChoice = (choice) => {
    setUpdateExisting(choice);
    setStep(4);
  };

  // Step 4: Choose audience
  const handleAudienceChoice = async () => {
    setLoading(true);
    try {
      let audienceId = selectedAudience;

      // Create new audience if needed
      if (createNewAudience && newAudienceName.trim()) {
        const response = await audiencesApi.create({
          name: newAudienceName.trim(),
          description: `Importado desde ${file?.name || 'archivo'}`
        });
        audienceId = response.data.audience.id;
        onRefreshAudiences();
      }

      setStep(5);

      // Execute import
      const response = await contactsApi.import(sessionId, { updateExisting }, audienceId || null);
      setImportResult(response.data);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar contactos');
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Show results and close
  const handleFinish = () => {
    onComplete();
    handleClose();
  };

  const steps = [
    { num: 1, title: 'Archivo' },
    { num: 2, title: 'Análisis' },
    { num: 3, title: 'Duplicados' },
    { num: 4, title: 'Audiencia' },
    { num: 5, title: 'Importar' },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" />
            Importar Contactos
          </DialogTitle>
          <DialogDescription>
            Importa contactos desde un archivo Excel o CSV
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center py-4">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step > s.num
                    ? 'bg-emerald-500 text-white'
                    : step === s.num
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 text-slate-400'
                    }`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`text-xs mt-1 ${step >= s.num ? 'text-slate-700' : 'text-slate-400'}`}>
                  {s.title}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-1 ${step > s.num ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Upload File */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-300 transition-colors">
                <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">
                  {file ? file.name : 'Selecciona un archivo'}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Formatos soportados: CSV, Excel (.xlsx, .xls)
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" asChild>
                    <span className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      {file ? 'Cambiar archivo' : 'Seleccionar archivo'}
                    </span>
                  </Button>
                </label>
              </div>

              {file && (
                <Card className="bg-slate-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-8 h-8 text-indigo-600" />
                      <div>
                        <p className="font-medium text-slate-900">{file.name}</p>
                        <p className="text-sm text-slate-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Analysis Results */}
          {step === 2 && analysisResult === null && (
            <div className="space-y-6 text-center py-8">
              {loading ? (
                <>
                  <Loader2 className="w-12 h-12 text-indigo-600 mx-auto animate-spin" />
                  <p className="text-slate-600">Analizando contactos...</p>
                </>
              ) : (
                <>
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">Archivo cargado correctamente</h3>
                    <p className="text-slate-600 mt-1">
                      Se encontraron <strong>{uploadResult?.totalRows || 0}</strong> contactos
                    </p>
                  </div>

                  {uploadResult?.preview?.length > 0 && (
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-700 mb-2">Vista previa:</p>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Email</TableHead>
                              <TableHead>Nombre</TableHead>
                              <TableHead>Apellido</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {uploadResult.preview.slice(0, 3).map((contact, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-sm">{contact.email}</TableCell>
                                <TableCell className="text-sm">{contact.firstName}</TableCell>
                                <TableCell className="text-sm">{contact.lastName}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Duplicate Decision */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-6 text-center">
                    <UserPlus className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-emerald-700">
                      {analysisResult?.newContacts || 0}
                    </p>
                    <p className="text-sm text-emerald-600">Contactos nuevos</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6 text-center">
                    <RefreshCw className="w-10 h-10 text-amber-600 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-amber-700">
                      {analysisResult?.duplicateContacts || 0}
                    </p>
                    <p className="text-sm text-amber-600">Contactos existentes</p>
                  </CardContent>
                </Card>
              </div>

              {analysisResult?.duplicateContacts > 0 && (
                <div className="space-y-4">
                  <p className="text-center text-slate-700 font-medium">
                    ¿Qué deseas hacer con los {analysisResult.duplicateContacts} contactos existentes?
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleUpdateChoice(true)}
                      className="p-4 border-2 rounded-xl text-left hover:border-indigo-500 hover:bg-indigo-50 transition-all"
                      data-testid="update-existing-btn"
                    >
                      <RefreshCw className="w-6 h-6 text-indigo-600 mb-2" />
                      <p className="font-medium text-slate-900">Actualizar existentes</p>
                      <p className="text-sm text-slate-500">
                        Actualiza los datos de los contactos que ya existen
                      </p>
                    </button>
                    <button
                      onClick={() => handleUpdateChoice(false)}
                      className="p-4 border-2 rounded-xl text-left hover:border-emerald-500 hover:bg-emerald-50 transition-all"
                      data-testid="skip-existing-btn"
                    >
                      <UserPlus className="w-6 h-6 text-emerald-600 mb-2" />
                      <p className="font-medium text-slate-900">Solo nuevos</p>
                      <p className="text-sm text-slate-500">
                        Importa solo los contactos que no existen
                      </p>
                    </button>
                  </div>
                </div>
              )}

              {analysisResult?.duplicateContacts === 0 && (
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-slate-700">
                    ¡Todos los contactos son nuevos! Puedes continuar.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Audience Selection */}
          {step === 4 && (
            <div className="space-y-6">
              <p className="text-center text-slate-700">
                ¿Deseas asignar estos contactos a una audiencia?
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    type="radio"
                    id="no-audience"
                    name="audience-choice"
                    checked={!createNewAudience && !selectedAudience}
                    onChange={() => {
                      setCreateNewAudience(false);
                      setSelectedAudience('');
                    }}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <label htmlFor="no-audience" className="text-slate-700">
                    No asignar a ninguna audiencia
                  </label>
                </div>

                <div className="flex items-center gap-4">
                  <input
                    type="radio"
                    id="existing-audience"
                    name="audience-choice"
                    checked={!createNewAudience && !!selectedAudience}
                    onChange={() => setCreateNewAudience(false)}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <label htmlFor="existing-audience" className="flex-1">
                    <span className="text-slate-700">Seleccionar audiencia existente</span>
                    <Select
                      value={selectedAudience}
                      onValueChange={(v) => {
                        setSelectedAudience(v);
                        setCreateNewAudience(false);
                      }}
                      disabled={createNewAudience}
                    >
                      <SelectTrigger className="mt-2" data-testid="select-audience">
                        <SelectValue placeholder="Selecciona una audiencia" />
                      </SelectTrigger>
                      <SelectContent>
                        {audiences.map((audience) => (
                          <SelectItem key={audience.id} value={audience.id}>
                            {audience.name} ({audience.contactCount || 0} contactos)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                </div>

                <div className="flex items-start gap-4">
                  <input
                    type="radio"
                    id="new-audience"
                    name="audience-choice"
                    checked={createNewAudience}
                    onChange={() => {
                      setCreateNewAudience(true);
                      setSelectedAudience('');
                    }}
                    className="w-4 h-4 text-indigo-600 mt-1"
                  />
                  <label htmlFor="new-audience" className="flex-1">
                    <span className="text-slate-700">Crear nueva audiencia</span>
                    <Input
                      placeholder="Nombre de la nueva audiencia"
                      value={newAudienceName}
                      onChange={(e) => setNewAudienceName(e.target.value)}
                      disabled={!createNewAudience}
                      className="mt-2"
                      data-testid="new-audience-name"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Import Results */}
          {step === 5 && (
            <div className="text-center py-8">
              {loading ? (
                <>
                  <Loader2 className="w-12 h-12 text-indigo-600 mx-auto animate-spin" />
                  <p className="text-slate-600 mt-4">Importando contactos...</p>
                </>
              ) : importResult ? (
                <>
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    ¡Importación completada!
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="p-4 bg-emerald-50 rounded-xl">
                      <p className="text-2xl font-bold text-emerald-700">{importResult.imported}</p>
                      <p className="text-sm text-emerald-600">Nuevos</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="text-2xl font-bold text-blue-700">{importResult.updated}</p>
                      <p className="text-sm text-blue-600">Actualizados</p>
                    </div>
                    <div className="p-4 bg-indigo-50 rounded-xl">
                      <p className="text-2xl font-bold text-indigo-700">{importResult.total}</p>
                      <p className="text-sm text-indigo-600">Total</p>
                    </div>
                  </div>
                </>
              ) : (
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <DialogFooter className="gap-2">
          {step > 1 && step < 5 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={loading}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>
          )}

          <div className="flex-1" />

          {step === 1 && (
            <Button onClick={handleUpload} disabled={!file || loading} data-testid="upload-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Subir archivo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 2 && !loading && (
            <Button onClick={handleAnalyze} disabled={loading} data-testid="analyze-btn">
              Analizar contactos
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 3 && analysisResult?.duplicateContacts === 0 && (
            <Button onClick={() => setStep(4)} data-testid="continue-btn">
              Continuar
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {step === 4 && (
            <Button
              onClick={handleAudienceChoice}
              disabled={loading || (createNewAudience && !newAudienceName.trim())}
              data-testid="import-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Importar contactos
            </Button>
          )}

          {step === 5 && importResult && (
            <Button onClick={handleFinish} data-testid="finish-btn">
              <Check className="w-4 h-4 mr-2" />
              Finalizar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Contacts Page
export const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [segments, setSegments] = useState([]);
  const [audiences, setAudiences] = useState([]); // Local audiences for import
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [showNewAudienceDialog, setShowNewAudienceDialog] = useState(false);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [isMauticConnected, setIsMauticConnected] = useState(false);
  const [totalContacts, setTotalContacts] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pageSize = 20;

  const fetchMauticData = useCallback(async (searchTerm = '', page = 0) => {
    try {
      setLoading(true);
      const start = page * pageSize;
      console.log(`[Contacts] Fetching data: search="${searchTerm}", page=${page}, start=${start}`);

      let contactsData = { contacts: [], total: 0, simulated: true };
      let segmentsData = { segments: [], simulated: true };
      let audiencesData = { audiences: [] };

      // 1. Fetch contacts
      try {
        const res = await mauticApi.getContacts({
          search: searchTerm || undefined,
          limit: pageSize,
          start
        });
        contactsData = res.data;
        console.log('[Contacts] Contacts loaded:', contactsData.contacts?.length);
      } catch (err) {
        console.error('[Contacts] Error fetching contacts:', err);
        toast.error(`Error al cargar contactos: ${err.response?.data?.error || err.message}`);
        throw err; // Re-throw to stop sequence
      }

      // 2. Fetch segments
      try {
        const res = await mauticApi.getSegments();
        segmentsData = res.data;
        console.log('[Contacts] Segments loaded:', segmentsData.segments?.length);
      } catch (err) {
        console.error('[Contacts] Error fetching segments:', err);
        // Don't necessarily throw, we can show contacts without segments
        toast.error(`Error al cargar segmentos: ${err.response?.data?.error || err.message}`);
      }

      // 3. Fetch audiences
      try {
        const res = await audiencesApi.list();
        audiencesData = res.data;
        console.log('[Contacts] Audiences loaded:', audiencesData.audiences?.length);
      } catch (err) {
        console.error('[Contacts] Error fetching audiences:', err);
        toast.error(`Error al cargar audiencias locales: ${err.response?.data?.error || err.message}`);
      }

      // Update state
      const isSimulated = contactsData.simulated || segmentsData.simulated;
      setIsMauticConnected(!isSimulated);

      setContacts(contactsData.contacts || []);
      setTotalContacts(parseInt(contactsData.total) || contactsData.contacts?.length || 0);
      setSegments(segmentsData.segments || []);
      setAudiences(audiencesData.audiences || []);

    } catch (error) {
      console.error('[Contacts] Global error in fetchMauticData:', error);
      // Main error toast already handled by individual try-catches or re-throw
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMauticData();
  }, [fetchMauticData]);

  // Debounced search
  const handleSearch = (value) => {
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      setCurrentPage(0);
      fetchMauticData(value, 0);
    }, 500);
    setSearchTimeout(timeout);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchMauticData(search, newPage);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setCurrentPage(0);
    fetchMauticData(search, 0);
  };

  const handleDeleteContact = async () => {
    if (!deletingContact) return;
    try {
      await contactsApi.delete(deletingContact.id);
      toast.success('Contacto eliminado');
      fetchMauticData(search, currentPage);
    } catch (error) {
      toast.error('Error al eliminar contacto');
    } finally {
      setDeletingContact(null);
    }
  };

  const handleCreateAudience = async () => {
    if (!newAudienceName.trim()) return;
    try {
      await audiencesApi.create({ name: newAudienceName.trim() });
      toast.success('Audiencia creada');
      setNewAudienceName('');
      setShowNewAudienceDialog(false);
      fetchMauticData(search, currentPage);
    } catch (error) {
      toast.error('Error al crear audiencia');
    }
  };

  const totalPages = Math.ceil(totalContacts / pageSize);
  const contactsWithEmail = contacts.filter(c => c.email).length;
  const contactsWithPhone = contacts.filter(c => c.phone).length;

  return (
    <div className="p-8 animate-fade-in" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Contactos</h1>
            {isMauticConnected ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Cloud className="w-3 h-3 mr-1" />
                Mautic conectado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-50 text-slate-500">
                <CloudOff className="w-3 h-3 mr-1" />
                Datos simulados
              </Badge>
            )}
          </div>
          <p className="text-slate-500 mt-1">
            {isMauticConnected
              ? 'Contactos sincronizados desde tu instancia de Mautic'
              : 'Conecta Mautic en Configuración para ver datos reales'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-contacts-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button variant="outline" onClick={() => setShowNewAudienceDialog(true)} data-testid="new-audience-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Audiencia
          </Button>
          <Button onClick={() => setShowImportWizard(true)} data-testid="import-contacts-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Contactos</p>
                <p className="text-3xl font-bold text-slate-900">{totalContacts}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Segmentos Mautic</p>
                <p className="text-3xl font-bold text-slate-900">{segments.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Con Email</p>
                <p className="text-3xl font-bold text-slate-900">{contactsWithEmail}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Con Teléfono</p>
                <p className="text-3xl font-bold text-slate-900">{contactsWithPhone}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Phone className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Segments from Mautic */}
      {segments.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Segmentos de Mautic</h2>
          <div className="flex flex-wrap gap-3">
            {segments.map((segment) => (
              <Badge
                key={segment.id}
                variant="outline"
                className="px-4 py-2 text-sm cursor-pointer hover:bg-slate-50"
              >
                {segment.name} ({segment.count || 0})
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar contactos..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
            data-testid="search-contacts"
          />
        </div>
      </div>

      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              <p className="text-slate-500 mt-2">Cargando contactos de Mautic...</p>
            </div>
          ) : contacts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Fecha de Alta</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{contact.email || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.firstName || contact.lastName
                        ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {contact.phone ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Phone className="w-3 h-3" />
                          {contact.phone}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {contact.dateAdded ? (
                        <span className="text-slate-500 text-sm">
                          {new Date(contact.dateAdded).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setDeletingContact(contact)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {search ? 'No se encontraron contactos' : 'No hay contactos'}
              </h3>
              <p className="text-slate-500 mb-4">
                {search ? 'Prueba con otra búsqueda' : 'Los contactos aparecerán aquí cuando estén en Mautic'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalContacts > pageSize && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">
            Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalContacts)} de {totalContacts} contactos
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <span className="text-sm text-slate-500 px-2">
              Página {currentPage + 1} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1 || loading}
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Import Wizard */}
      <ImportWizard
        open={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onComplete={() => fetchMauticData(search, currentPage)}
        audiences={audiences}
        onRefreshAudiences={() => fetchMauticData(search, currentPage)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingContact} onOpenChange={() => setDeletingContact(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar contacto?</AlertDialogTitle>
            <AlertDialogDescription>
              El contacto "{deletingContact?.email}" será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContact} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Audience Dialog */}
      <Dialog open={showNewAudienceDialog} onOpenChange={setShowNewAudienceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Audiencia</DialogTitle>
            <DialogDescription>Crea una nueva audiencia para segmentar tus contactos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="audience-name">Nombre de la audiencia</Label>
              <Input
                id="audience-name"
                value={newAudienceName}
                onChange={(e) => setNewAudienceName(e.target.value)}
                placeholder="Ej: Clientes VIP"
                className="mt-2"
                data-testid="audience-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAudienceDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateAudience} disabled={!newAudienceName.trim()}>
              Crear audiencia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
